import React, { Component } from 'react';

import Tooltip from '@mui/material/Tooltip';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faUndo, faFileDownload, faFileUpload, faTimes, faInfoCircle, faCircleExclamation, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faFlag as farFlag } from '@fortawesome/free-regular-svg-icons';
import { constants } from 'dataforazeroth-common';

import Tag from '../components/Tag';
import Progress from './Progress';
import Loading from '../components/Loading';
import Paging from '../components/Paging';
import NumberInput from '../components/NumberInput';
import ColorInput from '../components/ColorInput';
import CollapseButton from '../components/CollapseButton';
import FilterButton from '../components/FilterButton';
import SortButton from '../components/SortButton';
import ToggleButton from '../components/ToggleButton';
import ToggleButtonWrapper from './ToggleButtonWrapper';
import AddonUpload from './AddonUpload';

import metadata from '../util/metadata';
import login from '../util/login';
import utils from '../util/utils';
import download from '../util/download';

import './Collection.scss';

const DAY = 24 * 60 * 60 * 1000;
const COLORMIN = utils.colorDistance('101010', '000000');
const SORTS = {
    'name': (a, b) => utils.collator.compare(a.name, b.name),
    'source': (a, b) => utils.collator.compare(a.sourceicon, b.sourceicon) || utils.collator.compare(a.sourcezone, b.sourcezone) || utils.collator.compare(a.source, b.source) || utils.collator.compare(a.sourcestanding, b.sourcestanding),
    'profession': (a, b) => utils.collator.compare(a.prof || 'Other', b.prof || 'Other'),
    'category': (a, b) => utils.collator.compare(a.category || 'Unknown', b.category || 'Unknown') || utils.collator.compare(a.subcategory, b.subcategory),
    'rarity': (a, b) => (a.rarity || 0) - (b.rarity || 0),
    'id': (a, b) => (a.id || 0) - (b.id || 0),
    'patch': (a, b) => utils.collator.compare(a.patch, b.patch)
};

// create sorts for both directions
Object.keys(SORTS).forEach(key => {
    const fn = SORTS[key];
    SORTS[key + '-asc'] = fn;
    SORTS[key + '-desc'] = (a, b) => fn(b, a);
});

const GROUPS = {
    'source': a => utils.getFriendlySourceName(a.sourceicon),
    'profession': a => a.prof || 'Other',
    'category': a => a.category || 'Unknown',
    'patch': a => constants.majorVersions[a.patch] || 'v999'
};

// create sorts for each group
Object.keys(GROUPS).forEach(key => {
    const fn = GROUPS[key];
    GROUPS[key + '-asc'] = (a, b) => utils.collator.compare(fn(a), fn(b));
    GROUPS[key + '-desc'] = (a, b) => utils.collator.compare(fn(b), fn(a));
});

// special case - use expansion name
GROUPS['patch'] = a => constants.expansions[constants.majorVersions[a.patch]] || 'Unknown';
GROUPS['null'] = () => null;

const TABS = [ 'achievements', 'mounts', 'pets', 'titles', 'reputations', 'recipes', 'quests', 'toys', 'appearances', 'appearancesources', 'decor', 'heirlooms', 'hunterpets', 'soulshapes', 'manuscripts', 'skips', 'alts' ];
function nottabs(...tabs) {
    const set = new Set(TABS);
    for (let tab of tabs) {
        set.delete(tab);
    }
    return set;
}

const FILTERS = {
    // search bar
    collectedThis: {
        tabs: nottabs('alts'),
        include: (tpc, browse, compare) => !browse && !compare && !tpc.showCollectedThis,
        eval: (tpc, item) => item.character ? tpc.showCollectedThis : !tpc.showCollectedThis,
        reset: t => !t.props.collection.showCollectedThis ? t.toggleCollectedThis() : null,
        progress: true
    },
    collectedAlt: {
        tabs: nottabs('alts'),
        include: (tpc, browse, compare) => !browse && !compare && !tpc.showCollectedAlt,
        eval: (tpc, item) => (item.account && !item.character) ? tpc.showCollectedAlt : !tpc.showCollectedAlt,
        reset: t => !t.props.collection.showCollectedAlt ? t.toggleCollectedAlt() : null,
        progress: true
    },
    notCollected: {
        tabs: nottabs('alts'),
        include: (tpc, browse, compare) => !browse && !compare && !tpc.showNotCollected,
        eval: (tpc, item) => item.account ? !tpc.showNotCollected : tpc.showNotCollected,
        reset: t => !t.props.collection.showNotCollected ? t.toggleNotCollected() : null,
        progress: true
    },

    // LIMITED TIME
    remix: {
        label: `Remix`,
        tabs: new Set([ 'achievements', 'mounts', 'pets', 'titles', 'toys', 'appearances', 'appearancesources', 'decor' ]),
        include: tpc => tpc.showRemix,
        eval: (tpc, item) => metadata.remix[tpc.show]?.has(item.id),
        reset: t => t.props.collection.showRemix ? t.toggleRemix() : null
    },

    // filters
    factions: {
        label: 'Faction',
        tabs: new Set([ 'achievements', 'mounts', 'pets', 'titles', 'reputations', 'recipes', 'quests', 'toys', 'appearances', 'appearancesources', 'decor', 'heirlooms', 'alts' ]),
        include: tpc => tpc.hideFactions.size,
        eval: (tpc, item) => !tpc.hideFactions.has(item.faction === undefined ? '' : item.faction),
        default: t => !t.props.collection.hideFactions.size,
        reset: t => t.props.onToggle('options', 'hideFactions', 'All')
    },
    races: {
        label: 'Race',
        tabs: new Set([ 'quests', 'appearances', 'appearancesources' ]),
        include: tpc => tpc.hideRaces.size,
        eval: (tpc, item) => item.race ? !item.race.every(e => tpc.hideRaces.has(e)) : !tpc.hideRaces.has(''),
        default: t => !t.props.collection.hideRaces.size,
        reset: t => t.props.onToggle('options', 'hideRaces', 'All')
    },
    classes: {
        label: 'Class',
        tabs: new Set([ 'quests', 'appearances', 'appearancesources' ]),
        include: tpc => tpc.hideClasses.size,
        eval: (tpc, item) => item.class ? !item.class.every(e => tpc.hideClasses.has(e)) : !tpc.hideClasses.has(''),
        default: t => !t.props.collection.hideClasses.size,
        reset: t => t.props.onToggle('options', 'hideClasses', 'All')
    },
    tags: {
        label: 'Tags',
        tabs: nottabs('alts', 'appearances'),
        visible: t => login.jwt.get(),
        include: tpc => tpc.hideTags.size,
        eval: (tpc, item) => item.tags ? !item.tags.every(e => tpc.hideTags.has(e)) : !tpc.hideTags.has('No Tags'),
        default: t => t.props.collection.hideTags.size === 1 && t.props.collection.hideTags.has('Hidden'),
        reset: t => t.props.onToggle('tag', 'Reset', [ 'Hidden' ])
    },
    patches: {
        label: 'Expansion',
        tabs: nottabs('alts'),
        include: tpc => tpc.hidePatches.size,
        eval: (tpc, item) => !tpc.hidePatches.has(item.patch|| 'Other'),
        default: t => !t.props.collection.hidePatches.size,
        reset: t => t.props.onToggle('options', 'hidePatches', 'All')
    },
    sources: {
        label: 'Source',
        tabs: new Set([ 'mounts', 'pets', 'titles', 'recipes', 'toys', 'appearances', 'appearancesources', 'decor', 'heirlooms', 'soulshapes', 'manuscripts' ]),
        include: tpc => tpc.hideSources.size,
        eval: (tpc, item) => !tpc.hideSources.has(item.sourceicon || 'other'),
        default: t => !t.props.collection.hideSources.size,
        reset: t => t.props.onToggle('options', 'hideSources', 'All')
    },
    slots: {
        label: 'Slot',
        tabs: new Set([ 'appearances', 'appearancesources' ]),
        include: tpc => tpc.hideSlots.size,
        eval: (tpc, item) => !tpc.hideSlots.has(item.slot || 'Other'),
        default: t => !t.props.collection.hideSlots.size,
        reset: t => t.props.onToggle('options', 'hideSlots', 'All')
    },
    types: {
        label: 'Type',
        tabs: new Set([ 'appearances', 'appearancesources' ]),
        include: tpc => tpc.hideTypes.size,
        eval: (tpc, item) => !tpc.hideTypes.has(item.type || 'Other'),
        default: t => !t.props.collection.hideTypes.size,
        reset: t => t.props.onToggle('options', 'hideTypes', 'All')
    },
    qualities: {
        label: 'Quality',
        tabs: new Set([ 'pets', 'appearances', 'appearancesources' ]),
        visible: t => !t.props.collection.showPets || t.props.showCollected !== false,
        include: (tpc, browse) => tpc.hideQualities.size && (!tpc.showPets || !browse),
        eval: (tpc, item) => !tpc.hideQualities.has(item.quality || 0),
        default: t => !t.props.collection.hideQualities.size,
        reset: t => t.props.onToggle('options', 'hideQualities', 'All')
    },
    levels: {
        label: 'Level',
        tabs: new Set([ 'pets', 'heirlooms' ]),
        visible: t => t.props.showCollected !== false,
        include: (tpc, browse) => !browse && (tpc.level.mintext || tpc.level.maxtext),
        eval: (tpc, item) => {
            const level = item.level || (tpc.showPets ? 1 : 0);
            const min = tpc.level.mintext ? tpc.level.minnumber : -Infinity;
            const max = tpc.level.maxtext ? tpc.level.maxnumber : Infinity;
            return level >= min && level <= max;
        },
        default: t => !t.props.collection.level?.mintext && !t.props.collection.level?.maxtext,
        reset: t => t.props.onToggle('level', { mintext: '', maxtext: '' })
    },
    binds: {
        label: 'Binds',
        tabs: new Set([ 'recipes', 'appearances', 'appearancesources' ]),
        include: tpc => tpc.hideBinds.size,
        eval: (tpc, item) => !tpc.hideBinds.has(item.bind || 0),
        default: t => !t.props.collection.hideBinds.size,
        reset: t => t.props.onToggle('options', 'hideBinds', 'All')
    },
    categories: {
        label: 'Category',
        tabs: new Set([ 'achievements' ]),
        include: tpc => tpc.hideCategories.size,
        eval: (tpc, item) => !tpc.hideCategories.has(item.category),
        default: t => !t.props.collection.hideCategories.size,
        reset: t => t.props.onToggle('options', 'hideCategories', 'All')
    },
    zones: {
        label: 'Category',
        tabs: new Set([ 'quests' ]),
        include: tpc => tpc.hideZones.size,
        eval: (tpc, item) => !tpc.hideZones.has(item.category|| 'Unknown'),
        default: t => !t.props.collection.hideZones.size,
        reset: t => t.props.onToggle('options', 'hideZones', 'All')
    },
    professions: {
        label: 'Profession',
        tabs: new Set([ 'quests', 'recipes' ]),
        include: tpc => tpc.hideProfessions.size,
        eval: (tpc, item) => !tpc.hideProfessions.has(item.prof || ''),
        default: t => !t.props.collection.hideProfessions.size,
        reset: t => t.props.onToggle('options', 'hideProfessions', 'All')
    },
    rarities: {
        label: 'Rarity',
        tabs: new Set([ 'achievements', 'mounts', 'pets', 'titles', 'reputations', 'recipes', 'quests', 'toys', 'soulshapes', 'skips' ]),
        include: tpc => tpc.rarity.mintext || tpc.rarity.maxtext,
        eval: (tpc, item) => {
            const min = tpc.rarity.mintext ? tpc.rarity.minnumber : -Infinity;
            const max = tpc.rarity.maxtext ? tpc.rarity.maxnumber : Infinity;
            return item.rarity > min && item.rarity <= max;
        },
        default: t => !t.props.collection.rarity?.mintext && !t.props.collection.rarity?.maxtext,
        reset: t => t.props.onToggle('rarity', { mintext: '', maxtext: '' })
    },
    seen: {
        label: 'Seen',
        tabs: new Set([ 'achievements', 'mounts', 'pets', 'titles', 'reputations', 'recipes', 'quests', 'toys', 'soulshapes', 'skips' ]),
        include: tpc => tpc.seen.mintext || tpc.seen.maxtext,
        eval: (tpc, item) => {
            const seen = item.seen || 0;
            const min = tpc.seen.mintext ? Date.now() - DAY * tpc.seen.minnumber : -Infinity;
            const max = tpc.seen.maxtext ? Date.now() - DAY * tpc.seen.maxnumber : Infinity;
            return seen >= min && seen <= max;
        },
        default: t => !t.props.collection.seen?.mintext && !t.props.collection.seen?.maxtext,
        reset: t => t.props.onToggle('seen', { mintext: '', maxtext: '' })
    },

    // advanced
    obtainables: {
        label: 'Obtainable',
        tabs: nottabs('alts'),
        include: tpc => tpc.hideObtainables.size,
        eval: (tpc, item, browse, compare) => {
            // Unobtainable (Collected) = enabled && collected && unobtainable (except limited time)
            const unobtainableCollected = !browse && !compare && item.account && item.unobtainable && item.unobtainable !== 3;
            return !tpc.hideObtainables.has(unobtainableCollected ? 'uc' : item.unobtainable || '');
        },
        default: t => {
            const set = new Set(t.props.collection.hideObtainables);
            if (t.props.browse || t.props.split) set.delete('uc');
            return set.size === 1 && set.has(2);
        },
        reset: t => t.props.onToggle('obtainable', 'Reset', [ 2 ])
    },
    missing: {
        tabs: nottabs('alts'),
        include: tpc => !tpc.showPresent || !tpc.showMissing,
        eval: (tpc, item) => item.missing ? tpc.showMissing : tpc.showPresent,
        reset: t => {
            if (!t.props.collection.showPresent) t.togglePresent();
            if (!t.props.collection.showMissing) t.toggleMissing();
        }
    },
    excluded: {
        tabs: nottabs('alts'),
        include: tpc => !tpc.showIncluded || !tpc.showExcluded,
        eval: (tpc, item) => item.excluded ? tpc.showExcluded : tpc.showIncluded,
        reset: t => {
            if (!t.props.collection.showIncluded) t.toggleIncluded();
            if (t.props.collection.showExcluded) t.toggleExcluded();
        }
    },
    tradable: {
        tabs: new Set([ 'pets' ]),
        include: tpc => !tpc.showTradable || !tpc.showNotTradable,
        eval: (tpc, item) => item.tradable ? tpc.showTradable : tpc.showNotTradable,
        reset: t => {
            if (!t.props.collection.showTradable) t.toggleTradable();
            if (!t.props.collection.showNotTradable) t.toggleNotTradable();
        }
    },
    exclusive: {
        tabs: nottabs('alts'),
        include: tpc => tpc.showCollectedExclusive,
        eval: (tpc, item) => item.alt ? !tpc.showCollectedExclusive : tpc.showCollectedExclusive,
        reset: t => t.props.collection.showCollectedExclusive ? t.toggleCollectedExclusive() : null
    },

    // expensive filters last
    keywords: {
        tabs: new Set(TABS),
        include: tpc => tpc.regex,
        eval: (tpc, item) => item.fulltext?.match(tpc.regex),
        reset: t => t.props.onToggle('filter', '')
    },
    colors: {
        label: 'Color',
        tabs: new Set([ 'appearances', 'appearancesources' ]),
        include: tpc => tpc?.color?.rgb,
        eval: (tpc, item) => item.rgb?.some?.(c => utils.colorDistance(c, tpc.color.rgb) < COLORMIN), // item.rgb is prepared during indexing
        default: t => !t.props.collection.color?.text,
        reset: t => t.props.onToggle('color', { text: '' })
    },

    // fake filters for grouping
    advanced: {
        label: 'Advanced',
        tabs: nottabs('alts'),
        include: tpc => false,
        default: t =>
            t.props.collection.showPresent && t.props.collection.showMissing // present/missing group
            && t.props.collection.showIncluded && !t.props.collection.showExcluded // excluded group
            && (!t.props.collection.showPets || t.props.collection.showTradable) && (!t.props.collection.showPets || t.props.collection.showNotTradable) // tradable group
            && !t.props.collection.showCollectedExclusive // exclusive mode
    },
    rarityAndSeen: {
        label: 'Rarity',
        tabs: new Set([ 'achievements', 'mounts', 'pets', 'titles', 'reputations', 'recipes', 'quests', 'toys', 'soulshapes', 'skips' ]),
        include: tpc => false,
        default: t => !t.props.collection.rarity?.mintext && !t.props.collection.rarity?.maxtext && !t.props.collection.seen?.mintext && !t.props.collection.seen?.maxtext
    }
};

const DECORFILTERS = [ 'Size', 'Culture', 'Style', 'Theme', 'Holiday', 'Category', 'Usage' ];
for (let f of DECORFILTERS) {
    FILTERS['decor' + f] = {
        label: f,
        tabs: new Set([ 'decor' ]),
        include: tpc => tpc['hideDecor' + f].size,
        eval: (tpc, item) => item.wowtags?.[f] ? !item.wowtags[f].every(e => tpc['hideDecor' + f].has(e)) : !tpc['hideDecor' + f].has('Unknown'),
        default: t => !t.props.collection['hideDecor' + f].size,
        reset: t => t.props.onToggle('options', 'hideDecor' + f, 'All')
    };
}

export default class Collection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            zones: [],
            showZones: false,
            showFilter: false,
            showSort: false,
            showGroup: false,
            showUpload: false,
            filtered: null,
            collapsed: null,
            groups: null,
            totals: null
        };
    }

    componentDidMount() {
        this.compute();
    }

    componentDidUpdate(prevProps, prevState) {
        const reset = prevState.filtered && !this.collection();
        const change = this.props.collection?.version !== prevProps.collection?.version;

        if (reset || change) this.compute();
    }

    keepPoppersOpen = (event) => {
        event?.stopPropagation?.();
    }

    toggleRememberFilter = () => {
        if (localStorage.rememberFilters === 'true') {
            localStorage.removeItem('rememberFilters');
        } else {
            localStorage.setItem('rememberFilters', 'true');
        }
        this.setState({ rememberFilters: Date.now() }); // just to re-render
    }

    toggleCollectedThis = () => {
        this.props.onToggle('collectedThis');
    }

    toggleCollectedAlt = () => {
        this.props.onToggle('collectedAlt');
    }

    toggleNotCollected = () => {
        this.props.onToggle('notCollected');
    }

    // LIMITED TIME
    toggleRemix = () => {
        this.props.onToggle('remix');
    }

    toggleCollectedExclusive = () => {
        this.props.onToggle('collectedExclusive');
    }

    togglePresent = () => {
        this.props.onToggle('present');
    }

    toggleMissing = () => {
        this.props.onToggle('missing');
    }

    toggleIncluded = () => {
        this.props.onToggle('included');
    }

    toggleExcluded = () => {
        this.props.onToggle('excluded');
    }

    toggleTradable = () => {
        this.props.onToggle('tradable');
    }

    toggleNotTradable = () => {
        this.props.onToggle('notTradable');
    }

    toggleZones = async (event) => {
        // need to grab the event right away
        const showZones = this.state.showZones ? false : event.currentTarget;

        if (!this.state.zones?.length) {
            const zones = await metadata.questZones();
            this.setState({ zones });
        }

        this.setState({ showZones });
    }

    hideZones = (event) => {
        this.setState({ showZones: false });
    }

    toggleFilter = (event) => {
        const showFilter = this.state.showFilter ? false : event.currentTarget;
        this.setState({ showFilter });
    }

    hideFilter = (event) => {
        this.setState({ showFilter: false });
    }

    handleFilterClick = async (category, arg1, arg2) => {
        this.props.onToggle('drawer', category, arg1, arg2);

        // need to hide menu plus any children
        this.setState({ showFilter: false, showZones: false });
    }

    handleFilterOptionClick = async (arg1, arg2, arg3) => {
        this.props.onToggle('options', arg1, arg2, arg3);
    }

    toggleSort = (event) => {
        const showSort = this.state.showSort ? false : event.currentTarget;
        this.setState({ showSort });
    }

    hideSort = (event) => {
        this.setState({ showSort: false });
    }

    toggleGroup = (event) => {
        const showGroup = this.state.showGroup ? false : event.currentTarget;
        this.setState({ showGroup });
    }

    hideGroup = (event) => {
        this.setState({ showGroup: false });
    }

    changeRarityMin = (text, number) => {
        this.props.onToggle('rarity', { mintext: text, minnumber: number });
    }

    changeRarityMax = (text, number) => {
        this.props.onToggle('rarity', { maxtext: text, maxnumber: number });
    }

    changeSeenMin = (text, number) => {
        this.props.onToggle('seen', { mintext: text, minnumber: number });
    }

    changeSeenMax = (text, number) => {
        this.props.onToggle('seen', { maxtext: text, maxnumber: number });
    }

    changeLevelMin = (text, number) => {
        this.props.onToggle('level', { mintext: text, minnumber: number });
    }

    changeLevelMax = (text, number) => {
        this.props.onToggle('level', { maxtext: text, maxnumber: number });
    }

    changeColor = (text, color) => {
        text = text || '';
        const rgb = color?.hex ? utils.parseRgb(color.hex) : null;

        this.props.onToggle('color', { text, rgb });
    }

    filterVisible = (f) => {
        // most filters are simply on/off based on tab
        // but some have extra conditions we also need to check
        return f.tabs.has(this.props.collection.show) && (!f.visible || f.visible(this));
    }

    resetFilters = () => {
        Object.values(FILTERS).forEach(filter => filter.reset?.(this));
    }

    changeFilter = (event) => {
        this.props.onToggle('filter', event.target.value || '');
    }

    clearFilter = (event) => {
        this.props.onToggle('filter', '');
        document.querySelector('.search-input')?.focus();
    }

    setPage = (start, size) => {
        start = size * ~~(start / size);
        this.props.onToggle('page', start, size);
        utils.scrollTo('collection');
    }

    download = () => {
        download.collection(this.state.filtered, this.props.collection);
    }

    toggleUpload = () => {
        this.setState({ showUpload: !this.state.showUpload });
    }

    finishUpload = (uploaded) => {
        this.setState({ showUpload: false });
        if (uploaded === true) this.props.onToggle('uploaded');
    }

    collection = () => {
        // collection key matches the name of the tab
        const tpc = this.props.collection;
        if (tpc.show === 'stats') return null; // stats are stored in tpc, but should not be rendered as a collection
        return tpc[tpc.show];
    }

    sort = (collection) => {
        const tpc = this.props.collection;
        if (tpc.showAlts) return collection; // no sorting for alts

        const sort1 = GROUPS[tpc.group]; // need to sort by grouping first
        const sort2 = SORTS[tpc.sort];
        const sort3 = SORTS[tpc.sort?.endsWith('-asc') ? 'name-asc' : 'name-desc']; // tie-breaker
        return collection.sort((a, b) => sort1?.(a, b) || sort2?.(a, b) || sort3?.(a, b));
    }

    filter = (progress) => {
        const tpc = this.props.collection;
        const gather = Object.values(FILTERS).filter(f => Boolean(progress) === Boolean(f.progress) && this.filterVisible(f) && f.include(tpc, this.props.browse, this.props.split)).map(f => f.eval);
        return item => gather.every(fn => fn(tpc, item, this.props.browse, this.props.split));
    }

    stats = (collection, alsoFilter) => {
        // prep our grouping function
        const tpc = this.props.collection;
        let groupfn = GROUPS[utils.upto(tpc.group, '-')] || GROUPS['null'];
        if (tpc.showAlts) groupfn = GROUPS['null'];

        // do rollup
        const rollup = {};
        collection = collection.filter((item, idx) => {
            const current = item.group = groupfn(item);

            rollup[current] ??= { name: current, start: idx, total: 0, character: 0, account: 0, excluded: 0, alliance: 0, horde: 0, missing: 0 };
            rollup[current].total++;

            if (item.account) rollup[current].account++;
            if (item.character) rollup[current].character++;

            if (item.excluded) rollup[current].excluded++;
            if (item.missing) rollup[current].missing++;
            if (item.faction === 0) rollup[current].alliance++;
            if (item.faction === 1) rollup[current].horde++;

            if (item.unobtainable && !item.account) {
                const key = 'unobtainable' + item.unobtainable;
                rollup[current][key] = (rollup[current][key] || 0) + 1;
            }

            return alsoFilter ? alsoFilter(item) : true;
        });

        // compute overall totals
        const skip = new Set([ 'name', 'previous', 'next', 'start' ]);
        const totals = { name: 'Total' };
        const groups = Object.values(rollup).sort((a, b) => a.start - b.start);
        groups.forEach((entry, idx) => {
            // assign pointers
            entry.previous = groups[idx - 1];
            entry.next = groups[idx + 1];

            // sum up totals
            Object.keys(entry).filter(key => !skip.has(key)).forEach(key => totals[key] = (totals[key] || 0) + entry[key]);
        });

        return { collection, rollup, groups, totals };
    }

    // prepares rendering hints for progress bars
    hints = (collection, stats, displayStats) => {
        let last = '-start-';
        collection.forEach((item, idx) => {
            // reset any previous render hints
            item.before = null;
            item.after = null;

            // setup hints
            const current = item.group;
            if (current !== last) {
                item.before = stats.rollup[current];
                if (idx > 0) collection[idx - 1].after = stats.rollup[current];

                last = current;
            }
        });

        // link display values from the real ones
        Object.keys(stats.rollup).forEach(key => {
            stats.rollup[key].display = displayStats.rollup[key];
        });
        stats.totals.display = displayStats.totals;
    }

    compute = () => {
        const collection = this.collection();
        if (!collection) return this.setState({ filtered: null, collapsed: null, groups: null, totals: null });

        // first pass: don't filter collected status, generate progress bar stats
        const prep = this.sort(collection.filter(this.filter(false)));
        const displayStats = this.stats(prep, this.filter(true));
        const filtered = displayStats.collection;

        // second pass: finish filtering, generate paging stats
        const stats = this.stats(filtered, item => !this.props.collection.collapsed.has(item.group));
        const collapsed = stats.collection;

        // process rendering hints
        this.hints(filtered, stats, displayStats);

        this.setState({ filtered, collapsed, groups: stats.groups, totals: stats.totals });
    }

    // before shows group + anything collapsed before it
    before = (group) => {
        if (!group) return null;
        if (this.props.collection.collapsed.has(group.previous?.name)) return [ ...this.before(group.previous), group ];
        return [ group ];
    }

    // only show after if they are ALL collapsed
    after = (group) => {
        if (!group) return null;
        if (!this.props.collection.collapsed.has(group.name)) return null;
        if (!group.next) return [ group ];

        const array = this.after(group.next);
        return array ? [ group, ...array ] : null;
    }

    build = (page) => {
        const tpc = this.props.collection;

        if (!this.state.collapsed?.length && this.state.groups?.length) {
            // edge case - everything is collapsed
            return this.state.groups.map(group => <Progress key={group.name} group={group} groups={this.state.groups} collapsed={tpc.collapsed} browse={this.props.browse} split={this.props.split} onToggle={this.props.onToggle} />);
        }

        if (!page?.length) {
            return 'No matches found';
        }

        const showCollected = (this.props.showCollected !== false) && !tpc.showAlts;
        const showDetails = !tpc.showAlts && !tpc.showAppearances;

        return page.map(item =>
            <React.Fragment key={item.id}>
                {this.before(item.before)?.map(group => <Progress key={group.name} group={group} groups={this.state.groups} collapsed={tpc.collapsed} browse={this.props.browse} split={this.props.split} onToggle={this.props.onToggle} />)}
                <Tag item={item} showCollected={showCollected} showDetails={showDetails} showRarity={!tpc.showAlts} onCollectedBy={this.props.onCollectedBy} />
                {this.after(item.after)?.map(group => <Progress key={group.name} group={group} groups={this.state.groups} collapsed={tpc.collapsed} browse={this.props.browse} split={this.props.split} onToggle={this.props.onToggle} />)}
            </React.Fragment>
        );
    }

    render() {
        const tpc = this.props.collection;

        let collection = this.state.collapsed;
        if (!collection) return <Loading message={tpc.message} />;

        const showToggles = !this.props.split && !tpc.showAlts;
        const showCollected = (this.props.showCollected !== false);

        const totalSize = collection.length;
        const pageStart = tpc.start;
        let pageEnd = tpc.start + tpc.pageSize;
        collection = collection.slice(pageStart, pageEnd);
        pageEnd = pageStart + collection.length; // fix size for last page

        const contributor = login.contributor();
        const bulkEditCategory = contributor && !tpc.showAlts && collection?.[0]?.field;
        const bulkEditIds = contributor && collection.map(c => c.id).join('.');

        // only show upload/download for 1) applicable tabs 2) looking at own profile 3) on desktop
        const showDownload = !tpc.showAlts && this.props.myself && window.bootstrap.md.matches;
        const showUpload = (tpc.showManuscripts || tpc.showAppearancesources || tpc.showToys || tpc.showPets || tpc.showQuests) && this.props.myself && window.bootstrap.md.matches;
        const allFiltersInDefaultState = Object.values(FILTERS).every(filter => !filter.default || !this.filterVisible(filter) || filter.default(this));

        return (
            <div>
                {showToggles && !this.props.browse && tpc.showManuscripts ? <div className="mb-3">Blizzard's API does not currently provide a list of collected manuscripts. You can instead upload data from an addon. <Link className="small" to="/about#upload">More Info...</Link></div> : null}
                {showToggles && !this.props.browse && tpc.showDecor ? <div className="mb-3">Blizzard's API does not currently provide a list of collected housing decor. Data for Azeroth tries to guess what's collected based on achievements and quests, with more improvements coming soon!</div> : null}
                {showToggles && !this.props.browse && tpc.showAppearancesources ? <div className="mb-3">Blizzard's API does not currently provide a list of collected appearance sources. Your leaderboard count is obtained from achievement data. You can upload data from an addon. <Link className="small" to="/about#upload">More Info...</Link></div> : null}

                <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                    <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100') + ' flex-wrap mb-3 mr-3 position-relative'}>
                        <input type="text" placeholder="Search" className={'form-control search-input pr-4' + (window.bootstrap.md.matches ? '' : ' flex-fill')} value={tpc.filter} onChange={this.changeFilter} />
                        <button type="button" title="Clear" className="btn btn-clear" onClick={this.clearFilter}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    {showToggles && showCollected ?
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <ToggleButton label="Collected" checked={tpc.showCollectedThis} onToggle={this.toggleCollectedThis} />
                            <ToggleButton label="Collected on Alt" checked={tpc.showCollectedAlt} onToggle={this.toggleCollectedAlt} />
                            <ToggleButton label="Not Collected" checked={tpc.showNotCollected} onToggle={this.toggleNotCollected} />
                        </div>
                    : null}

                    {/* LIMITED TIME */}
                    {FILTERS.remix.tabs.has(tpc.show) ?
                        <Tooltip title={<><div>Will show only the items available during the Legion: Remix event.</div><div className="mt-3">To ensure the full list is shown, clear any other filters.</div></>}>
                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                                <ToggleButton label="Remix" checked={tpc.showRemix} onToggle={this.toggleRemix} />
                            </div>
                        </Tooltip>
                    : null}

                    {tpc.showAlts ? null :
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <CollapseButton label={allFiltersInDefaultState ? 'Filter' : <><FontAwesomeIcon icon={faCircleExclamation} /> Filter</>} open={this.state.showFilter} onToggle={this.toggleFilter} />
                            <CollapseButton label="Sort" open={this.state.showSort} onToggle={this.toggleSort} />
                            <CollapseButton label="Group" open={this.state.showGroup} onToggle={this.toggleGroup} />
                            <Tooltip title="Reset All Filters">
                                <button type="button" className="btn btn-primary" onClick={this.resetFilters}><FontAwesomeIcon icon={faUndo} /></button>
                            </Tooltip>
                        </div>
                    }

                    {!this.props.split && (tpc.showAppearancesources || tpc.showAppearances) ?
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                            {tpc.showAppearancesources ? <Link to="appearances" className="btn btn-primary"><FontAwesomeIcon icon={faCircle} /> Unique Only</Link> : null}
                            {tpc.showAppearances ? <Link to="appearancesources" className="btn btn-primary active"><FontAwesomeIcon icon={faCheckCircle} /> Unique Only</Link> : null}
                        </div>
                    : null}

                    {showDownload || showUpload ?
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            {showDownload ? <Tooltip title="Download CSV File"><button type="button" className="btn btn-primary" onClick={this.download}><FontAwesomeIcon icon={faFileDownload} /></button></Tooltip> : null}
                            {showUpload ? <Tooltip title="Upload Addon Data"><button type="button" className="btn btn-primary" onClick={this.toggleUpload}><FontAwesomeIcon icon={faFileUpload} /></button></Tooltip> : null}
                        </div>
                    : null}

                    {bulkEditCategory ?
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <Tooltip title="Bulk Report">
                                <Link to={'/collections/' + bulkEditCategory + '/' + bulkEditIds} target="_blank" className="btn btn-primary"><FontAwesomeIcon icon={farFlag} /></Link>
                            </Tooltip>
                        </div>
                    : null}
                </div>
                {tpc.showFactions && this.filterVisible(FILTERS.factions) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Faction</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <CollapseButton onToggle={this.props.onToggle} drawer="showFactions" />
                            {tpc.showAlts ? null : <ToggleButtonWrapper id="" label="Neutral" property="faction" checked={!tpc.hideFactions.has('')} onToggle={this.props.onToggle} />}
                            <ToggleButtonWrapper id={0} label="Alliance" property="faction" checked={!tpc.hideFactions.has(0)} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id={1} label="Horde" property="faction" checked={!tpc.hideFactions.has(1)} onToggle={this.props.onToggle} />
                        </div>
                    </div>
                : null}
                {tpc.showRaces && this.filterVisible(FILTERS.races) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Race</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showRaces" />
                                <ToggleButtonWrapper id="All" property="race" variants={constants.races.map(c => c.key)} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="race" variants={constants.races.map(c => c.key)} onToggle={this.props.onToggle} />
                            </div>
                            {constants.races.map(c => <ToggleButtonWrapper key={c.key} id={c.key} label={c.value} property="race" checked={!tpc.hideRaces.has(c.key)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showClasses && this.filterVisible(FILTERS.classes) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Class</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showClasses" />
                                <ToggleButtonWrapper id="All" property="class" variants={utils.classes.map(c => c.key)} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="class" variants={utils.classes.map(c => c.key)} onToggle={this.props.onToggle} />
                            </div>
                            {utils.classes.map(c => <ToggleButtonWrapper key={c.key} id={c.key} label={c.value} property="class" checked={!tpc.hideClasses.has(c.key)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showSources && this.filterVisible(FILTERS.sources) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Source</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showSources" />
                                <ToggleButtonWrapper id="All" property="source" variants={utils.sourceicons} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="source" variants={utils.sourceicons} onToggle={this.props.onToggle} />
                            </div>
                            {utils.sourceicons.map(key => <ToggleButtonWrapper key={key} id={key} label={utils.getFriendlySourceName(key)} property="source" checked={!tpc.hideSources.has(key)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showBinds && this.filterVisible(FILTERS.binds) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Binds</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <CollapseButton onToggle={this.props.onToggle} drawer="showBinds" />
                            <ToggleButtonWrapper id={0} label="on Pickup" property="bind" checked={!tpc.hideBinds.has(0)} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id={1} label={tpc.showAppearances || tpc.showAppearancesources ? 'on Equip' : 'on Use'} property="bind" checked={!tpc.hideBinds.has(1)} onToggle={this.props.onToggle} />
                        </div>
                    </div>
                : null}
                {tpc.showProfessions && this.filterVisible(FILTERS.professions) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Profession</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showProfessions" />
                                <ToggleButtonWrapper id="All" property="profession" variants={[ '', 'Alchemy', 'Archaeology', 'Blacksmithing', 'Cooking', 'Enchanting', 'Engineering',  'Goblin Engineering',  'Gnomish Engineering', 'Fishing', 'Herbalism', 'Inscription', 'Jewelcrafting', 'Leatherworking', 'Mining', 'Skinning', 'Tailoring' ]} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="profession" variants={[ '', 'Alchemy', 'Archaeology', 'Blacksmithing', 'Cooking', 'Enchanting', 'Engineering',  'Goblin Engineering',  'Gnomish Engineering', 'Fishing', 'Herbalism', 'Inscription', 'Jewelcrafting', 'Leatherworking', 'Mining', 'Skinning', 'Tailoring' ]} onToggle={this.props.onToggle} />
                            </div>
                            {tpc.showQuests ? <ToggleButtonWrapper id="" label="Neutral" property="profession" checked={!tpc.hideProfessions.has('')} onToggle={this.props.onToggle} /> : null}
                            <ToggleButtonWrapper id="Alchemy" property="profession" checked={!tpc.hideProfessions.has('Alchemy')} onToggle={this.props.onToggle} />
                            {tpc.showQuests ? <ToggleButtonWrapper id="Archaeology" property="profession" checked={!tpc.hideProfessions.has('Archaeology')} onToggle={this.props.onToggle} /> : null}
                            <ToggleButtonWrapper id="Blacksmithing" property="profession" checked={!tpc.hideProfessions.has('Blacksmithing')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Cooking" property="profession" checked={!tpc.hideProfessions.has('Cooking')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Enchanting" property="profession" checked={!tpc.hideProfessions.has('Enchanting')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Engineering" property="profession" checked={!tpc.hideProfessions.has('Engineering')} variants={[ 'Engineering', 'Goblin Engineering', 'Gnomish Engineering' ]} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Fishing" property="profession" checked={!tpc.hideProfessions.has('Fishing')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Herbalism" property="profession" checked={!tpc.hideProfessions.has('Herbalism')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Inscription" property="profession" checked={!tpc.hideProfessions.has('Inscription')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Jewelcrafting" property="profession" checked={!tpc.hideProfessions.has('Jewelcrafting')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Leatherworking" property="profession" checked={!tpc.hideProfessions.has('Leatherworking')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Mining" property="profession" checked={!tpc.hideProfessions.has('Mining')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Skinning" property="profession" checked={!tpc.hideProfessions.has('Skinning')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id="Tailoring" property="profession" checked={!tpc.hideProfessions.has('Tailoring')} onToggle={this.props.onToggle} />
                        </div>
                    </div>
                : null}
                {tpc.showCategories && this.filterVisible(FILTERS.categories) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Category</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showCategories" />
                                <ToggleButtonWrapper id="All" property="category" variants={tpc.categories} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="category" variants={tpc.categories} onToggle={this.props.onToggle} />
                            </div>
                            {tpc.categories?.map(c => <ToggleButtonWrapper key={c} id={c} property="category" checked={!tpc.hideCategories.has(c)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showSlots && this.filterVisible(FILTERS.slots) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Slot</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showSlots" />
                                <ToggleButtonWrapper id="All" property="slot" variants={utils.slots} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="slot" variants={utils.slots} onToggle={this.props.onToggle} />
                            </div>
                            {utils.slots.map(c => <ToggleButtonWrapper key={c} id={c} property="slot" checked={!tpc.hideSlots.has(c)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showTypes && this.filterVisible(FILTERS.types) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Type</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showTypes" />
                                <ToggleButtonWrapper id="All" property="type" variants={utils.geartypes} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="type" variants={utils.geartypes} onToggle={this.props.onToggle} />
                            </div>
                            {utils.geartypes.map(c => <ToggleButtonWrapper key={c} id={c} property="type" checked={!tpc.hideTypes.has(c)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showQualities && this.filterVisible(FILTERS.qualities) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Quality</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showQualities" />
                                <ToggleButtonWrapper id="All" property="quality" variants={utils.qualities.map(c => c.key)} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="quality" variants={utils.qualities.map(c => c.key)} onToggle={this.props.onToggle} />
                            </div>
                            {utils.qualities.filter(c => !tpc.showPets || c.key < 4).map(c => <ToggleButtonWrapper key={c.key} id={c.key} label={c.value} property="quality" checked={!tpc.hideQualities.has(c.key)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                <Popper open={this.state.showZones && this.props.split !== 'right'} anchorEl={this.state.showZones} placement="right">
                    <ClickAwayListener onClickAway={this.hideZones} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                        <div className="card mt-1">
                            <div className="btn-group-vertical" onMouseDown={this.keepPoppersOpen} onTouchStart={this.keepPoppersOpen}>
                                <div className="btn-group">
                                    <ToggleButtonWrapper id="All" property="zone" variants={this.state.zones} onToggle={this.props.onToggle} />
                                    <ToggleButtonWrapper id="None" property="zone" variants={this.state.zones} onToggle={this.props.onToggle} />
                                </div>
                                <div className="btn-group-vertical scroller">
                                    {this.state.zones.map(c => <ToggleButtonWrapper key={c} id={c} property="zone" checked={!tpc.hideZones.has(c)} onToggle={this.props.onToggle} />)}
                                </div>
                            </div>
                        </div>
                    </ClickAwayListener>
                </Popper>
                {tpc.showPatches && this.filterVisible(FILTERS.patches) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Expansion</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showPatches" />
                                <ToggleButtonWrapper id="All" property="patch" variants={constants.patches.all} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="patch" variants={constants.patches.all} onToggle={this.props.onToggle} />
                            </div>
                            {Object.keys(constants.expansions).map(ver => <ToggleButtonWrapper key={ver} id={constants.expansions[ver]} property="patch" checked={!tpc.hidePatches.has(constants.patches[ver][0])} variants={constants.patches[ver]} onToggle={this.props.onToggle} />)}
                        </div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                            {constants.patches.v11.filter(p => !p.includes('x')).map(p => <ToggleButtonWrapper key={p} id={p} property="patch" checked={!tpc.hidePatches.has(p)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {tpc.showRarity && this.filterVisible(FILTERS.rarities) ?
                    <div className={window.bootstrap.md.matches ? 'd-flex' : ''} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="mb-3">
                            <div className="small mb-1">Rarity</div>
                            <div>
                                <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100 mb-3')  + ' flex-wrap mr-3'}>
                                    <CollapseButton onToggle={this.props.onToggle} drawer="showRarity" />
                                </div>
                                <div className="btn-group mr-3">
                                    <div className="my-auto"><NumberInput placeholder="Min" className="form-control" value={tpc.rarity.mintext} onChange={this.changeRarityMin} /></div>
                                    <div className="my-auto mx-1">-</div>
                                    <div className="my-auto"><NumberInput placeholder="Max" className="form-control" value={tpc.rarity.maxtext} onChange={this.changeRarityMax} /></div>
                                    <div className="my-auto ml-1">%</div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="small mb-1">Seen</div>
                            <div className="btn-group">
                                <div className="my-auto"><NumberInput placeholder="From" className="form-control" value={tpc.seen.maxtext} onChange={this.changeSeenMax} /></div>
                                <div className="my-auto mx-1">-</div>
                                <div className="my-auto"><NumberInput placeholder="To" className="form-control" value={tpc.seen.mintext} onChange={this.changeSeenMin} /></div>
                                <div className="my-auto ml-1">days ago</div>
                            </div>
                        </div>
                    </div>
                : null}
                {tpc.showLevels && this.filterVisible(FILTERS.levels) ?
                    <div className={window.bootstrap.md.matches ? 'd-flex' : ''} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="mb-3">
                            <div className="small mb-1">Level</div>
                            <div>
                                <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100 mb-3')  + ' flex-wrap mr-3'}>
                                    <CollapseButton onToggle={this.props.onToggle} drawer="showLevels" />
                                </div>
                                <div className="btn-group">
                                    <div className="my-auto"><NumberInput placeholder="From" className="form-control" value={tpc.level.mintext} onChange={this.changeLevelMin} /></div>
                                    <div className="my-auto mx-1">-</div>
                                    <div className="my-auto"><NumberInput placeholder="To" className="form-control" value={tpc.level.maxtext} onChange={this.changeLevelMax} /></div>
                                </div>
                            </div>
                        </div>
                    </div>
                : null}
                {tpc.showColors && this.filterVisible(FILTERS.colors) ?
                    <div className={window.bootstrap.md.matches ? 'd-flex' : ''} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="mb-3">
                            <div className="small mb-1">Color</div>
                            <div>
                                <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100 mb-3')  + ' flex-wrap mr-3'}>
                                    <CollapseButton onToggle={this.props.onToggle} drawer="showColors" />
                                </div>
                                <div className="btn-group">
                                    <ColorInput value={tpc?.color?.text || ''} onChange={this.changeColor} />
                                </div>
                            </div>
                        </div>
                    </div>
                : null}
                {tpc.showTags && this.filterVisible(FILTERS.tags) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1"><Tooltip title={<>Add your own tags by clicking <i>More</i> in the item's tooltip!</>}>Tags <FontAwesomeIcon icon={faInfoCircle} /></Tooltip></div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <div className="btn-group">
                                <CollapseButton onToggle={this.props.onToggle} drawer="showTags" />
                                <ToggleButtonWrapper id="All" property="tag" variants={tpc.tags} onToggle={this.props.onToggle} />
                                <ToggleButtonWrapper id="None" property="tag" variants={tpc.tags} onToggle={this.props.onToggle} />
                            </div>
                            {tpc.tags?.map(tag => <ToggleButtonWrapper key={tag} id={tag} label={tag} property="tag" checked={!tpc.hideTags.has(tag)} onToggle={this.props.onToggle} />)}
                        </div>
                    </div>
                : null}
                {DECORFILTERS.map(f =>
                    (tpc['showDecor' + f] && this.filterVisible(FILTERS['decor' + f])) ?
                        <div key={f} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                            <div className="small mb-1">{f}</div>
                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                                <CollapseButton onToggle={this.props.onToggle} drawer={'showDecor' + f} />
                                <ToggleButtonWrapper id="All" property={'hideDecor' + f} variants={tpc['optionsDecor' + f]} onToggle={this.handleFilterOptionClick} />
                                <ToggleButtonWrapper id="None" property={'hideDecor' + f} variants={tpc['optionsDecor' + f]} onToggle={this.handleFilterOptionClick} />
                                {tpc['optionsDecor' + f].map(tag => <ToggleButtonWrapper key={tag} id={tag} label={tag} property={'hideDecor' + f} checked={!tpc['hideDecor' + f].has(tag)} onToggle={this.handleFilterOptionClick} />)}
                            </div>
                        </div>
                    : null
                )}
                {tpc.showObtainables && this.filterVisible(FILTERS.obtainables) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Obtainable</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                            <CollapseButton onToggle={this.props.onToggle} drawer="showObtainables" />
                            <ToggleButtonWrapper id="" label="Obtainable" property="obtainable" checked={!tpc.hideObtainables.has('')} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id={3} label="Obtainable for Limited Time" property="obtainable" checked={!tpc.hideObtainables.has(3)} onToggle={this.props.onToggle} />
                            {showToggles && showCollected ? <ToggleButtonWrapper id="uc" label="Unobtainable (Collected)" property="obtainable" checked={!tpc.hideObtainables.has('uc')} onToggle={this.props.onToggle} /> : null}
                            <ToggleButtonWrapper id={1} label="Unobtainable" property="obtainable" checked={!tpc.hideObtainables.has(1)} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id={4} label="Bugged" property="obtainable" checked={!tpc.hideObtainables.has(4)} onToggle={this.props.onToggle} />
                            <ToggleButtonWrapper id={2} label="Never Implemented" property="obtainable" checked={!tpc.hideObtainables.has(2)} onToggle={this.props.onToggle} />

                            {/*utils.obtainables.map(o => <ToggleButtonWrapper key={o.key} id={o.key} label={o.value} property="obtainable" checked={!tpc.hideObtainables.has(o.key)} onToggle={this.props.onToggle} />)*/}
                        </div>
                    </div>
                : null}
                {tpc.showAdvanced && this.filterVisible(FILTERS.advanced) ?
                    <div style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <div className="small mb-1">Advanced</div>
                        <div>
                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'}>
                                <CollapseButton onToggle={this.props.onToggle} drawer="showAdvanced" />
                            </div>

                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                                <button type="button" className={tpc.showPresent ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.togglePresent}><FontAwesomeIcon icon={tpc.showPresent ? faCheckCircle : faCircle} /> Present in API</button>
                                <button type="button" className={tpc.showMissing ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleMissing}><FontAwesomeIcon icon={tpc.showMissing ? faCheckCircle : faCircle} /> Missing from API</button>
                            </div>

                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                                <button type="button" className={tpc.showIncluded ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleIncluded}><FontAwesomeIcon icon={tpc.showIncluded ? faCheckCircle : faCircle} /> Included in Ranking</button>
                                <button type="button" className={tpc.showExcluded ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleExcluded}><FontAwesomeIcon icon={tpc.showExcluded ? faCheckCircle : faCircle} /> Excluded from Ranking</button>
                            </div>

                            {tpc.showPets ?
                                <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                                    <button type="button" className={tpc.showTradable ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleTradable}><FontAwesomeIcon icon={tpc.showTradable ? faCheckCircle : faCircle} /> Tradable</button>
                                    <button type="button" className={tpc.showNotTradable ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleNotTradable}><FontAwesomeIcon icon={tpc.showNotTradable ? faCheckCircle : faCircle} /> Not Tradable</button>
                                </div>
                            : null}

                            {showToggles && showCollected ?
                                <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mb-3 mr-3'} style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                                    <button type="button" className={tpc.showCollectedExclusive ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleCollectedExclusive}><FontAwesomeIcon icon={tpc.showCollectedExclusive ? faCheckCircle : faCircle} /> Exclusive to This Character</button>
                                </div>
                            : null}
                        </div>
                    </div>
                : null}
                <Popper open={this.state.showFilter && this.props.split !== 'right'} anchorEl={this.state.showFilter} placement="bottom-start">
                    <ClickAwayListener onClickAway={this.hideFilter} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                        <div className="card mt-1">
                            <div className="btn-group-vertical scroller" onMouseDown={this.keepPoppersOpen} onTouchStart={this.keepPoppersOpen}>
                                <FilterButton label="Faction" open={tpc.showFactions} default={!tpc.hideFactions.size} onToggle={this.handleFilterClick} arg="showFactions" visible={this.filterVisible(FILTERS.factions)} />
                                <FilterButton label="Race" open={tpc.showRaces} default={!tpc.hideRaces.size} onToggle={this.handleFilterClick} arg="showRaces" visible={this.filterVisible(FILTERS.races)} />
                                <FilterButton label="Class" open={tpc.showClasses} default={!tpc.hideClasses.size} onToggle={this.handleFilterClick} arg="showClasses" visible={this.filterVisible(FILTERS.classes)} />
                                <FilterButton label="Expansion" open={tpc.showPatches} default={!tpc.hidePatches.size} onToggle={this.handleFilterClick} arg="showPatches" visible={this.filterVisible(FILTERS.patches)} />
                                <FilterButton label="Source" open={tpc.showSources} default={!tpc.hideSources.size} onToggle={this.handleFilterClick} arg="showSources" visible={this.filterVisible(FILTERS.sources)} />
                                <FilterButton label="Color" open={tpc.showColors} default={FILTERS.colors.default(this)} onToggle={this.handleFilterClick} arg="showColors" visible={this.filterVisible(FILTERS.colors)} />
                                <FilterButton label="Slot" open={tpc.showSlots} default={!tpc.hideSlots.size} onToggle={this.handleFilterClick} arg="showSlots" visible={this.filterVisible(FILTERS.slots)} />
                                <FilterButton label="Type" open={tpc.showTypes} default={!tpc.hideTypes.size} onToggle={this.handleFilterClick} arg="showTypes" visible={this.filterVisible(FILTERS.types)} />
                                <FilterButton label="Quality" open={tpc.showQualities} default={!tpc.hideQualities.size} onToggle={this.handleFilterClick} arg="showQualities" visible={this.filterVisible(FILTERS.qualities)} />
                                <FilterButton label="Level" open={tpc.showLevels} default={FILTERS.levels.default(this)} onToggle={this.handleFilterClick} arg="showLevels" visible={this.filterVisible(FILTERS.levels)} />
                                <FilterButton label="Binds" open={tpc.showBinds} default={!tpc.hideBinds.size} onToggle={this.handleFilterClick} arg="showBinds" visible={this.filterVisible(FILTERS.binds)} />
                                <FilterButton label="Category" open={tpc.showCategories} default={!tpc.hideCategories.size} onToggle={this.handleFilterClick} arg="showCategories" visible={this.filterVisible(FILTERS.categories)} />
                                <FilterButton label={<span>Category <FontAwesomeIcon icon={faChevronRight} /></span>} open={this.state.showZones} default={!tpc.hideZones.size} onToggle={this.toggleZones} visible={this.filterVisible(FILTERS.zones)} />
                                <FilterButton label="Profession" open={tpc.showProfessions} default={!tpc.hideProfessions.size} onToggle={this.handleFilterClick} arg="showProfessions" visible={this.filterVisible(FILTERS.professions)} />
                                <FilterButton label="Rarity" open={tpc.showRarity} default={FILTERS.rarityAndSeen.default(this)} onToggle={this.handleFilterClick} arg="showRarity" visible={this.filterVisible(FILTERS.rarities)} />

                                {DECORFILTERS.map(f => <FilterButton key={f} label={f} open={tpc['showDecor' + f]} default={!tpc['hideDecor' + f].size} onToggle={this.handleFilterClick} arg={'showDecor' + f} visible={this.filterVisible(FILTERS['decor' + f])} />)}

                                <FilterButton label="Tags" open={tpc.showTags} default={FILTERS.tags.default(this)} onToggle={this.handleFilterClick} arg="showTags" visible={this.filterVisible(FILTERS.tags)} />
                                <FilterButton label="Obtainable" open={tpc.showObtainables} default={FILTERS.obtainables.default(this)} onToggle={this.handleFilterClick} arg="showObtainables" visible={this.filterVisible(FILTERS.obtainables)} />
                                <FilterButton label="Advanced" open={tpc.showAdvanced} default={FILTERS.advanced.default(this)} onToggle={this.handleFilterClick} arg="showAdvanced" visible={this.filterVisible(FILTERS.advanced)} />

                                <button type="button" className={localStorage.rememberFilters ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleRememberFilter}><FontAwesomeIcon icon={localStorage.rememberFilters ? faCheckCircle : faCircle} /> Remember Filters</button>
                            </div>
                        </div>
                    </ClickAwayListener>
                </Popper>
                <Popper open={this.state.showSort && this.props.split !== 'right'} anchorEl={this.state.showSort} placement="bottom-start">
                    <ClickAwayListener onClickAway={this.hideSort} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                        <div className="card mt-1">
                            <div className="btn-group-vertical scroller" onMouseDown={this.keepPoppersOpen} onTouchStart={this.keepPoppersOpen}>
                                <SortButton label="Name" sort="name" active={tpc.sort} visible={tpc.showSortName} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="Source" sort="source" active={tpc.sort} visible={tpc.showSortSource} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="Profession" sort="profession" active={tpc.sort} visible={tpc.showSortProfession} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="Category" sort="category" active={tpc.sort} visible={tpc.showSortCategory} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="Rarity" sort="rarity" active={tpc.sort} visible={tpc.showSortRarity} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="Patch" sort="patch" active={tpc.sort} visible={tpc.showSortPatch} onToggle={this.props.onToggle} arg="sortby" />
                                <SortButton label="ID" sort="id" active={tpc.sort} visible={tpc.showSortId} onToggle={this.props.onToggle} arg="sortby" />
                            </div>
                        </div>
                    </ClickAwayListener>
                </Popper>
                <Popper open={this.state.showGroup && this.props.split !== 'right'} anchorEl={this.state.showGroup} placement="bottom-start">
                    <ClickAwayListener onClickAway={this.hideGroup} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                        <div className="card mt-1">
                            <div className="btn-group-vertical scroller" onMouseDown={this.keepPoppersOpen} onTouchStart={this.keepPoppersOpen}>
                                <SortButton label="None" sort="null" active={tpc.group} visible={true} onToggle={this.props.onToggle} arg="groupby" />
                                <SortButton label="Source" sort="source" active={tpc.group} visible={tpc.showSortSource} onToggle={this.props.onToggle} arg="groupby" />
                                <SortButton label="Profession" sort="profession" active={tpc.group} visible={tpc.showSortProfession} onToggle={this.props.onToggle} arg="groupby" />
                                <SortButton label="Category" sort="category" active={tpc.group} visible={tpc.showSortCategory} onToggle={this.props.onToggle} arg="groupby" />
                                <SortButton label="Expansion" sort="patch" active={tpc.group} visible={tpc.showSortPatch} onToggle={this.props.onToggle} arg="groupby" />
                            </div>
                        </div>
                    </ClickAwayListener>
                </Popper>
                <Loading message={tpc.message} />
                <div id="collection" className={'collection' + (this.props.split ? '' : ' collection-nosplit')}>
                    {pageStart === 0 ? <Progress group={this.state.totals} groups={this.state.groups} collapsed={tpc.collapsed} onToggle={this.props.onToggle} browse={this.props.browse} split={this.props.split} total={true} filtered={this.collection()?.length - this.state.totals?.display?.total} /> : null}
                    {this.build(collection)}
                </div>
                {collection.length > 0 ?
                    <div className="mt-3" style={(this.props.split === 'right') ? { visibility: 'hidden' } : null}>
                        <Paging start={pageStart} end={pageEnd} pageSize={tpc.pageSize} pageSizes={[ 40, 60, 120, 180 ]} totalSize={totalSize} onClick={this.setPage} />
                    </div>
                : null}
                <AddonUpload open={this.state.showUpload} category={tpc.show} onClose={this.finishUpload} />
            </div>
        );
    }
}